import { useRef, useState } from "react";
import { isConvex, type Pt, type Quad } from "../../lib/scan/geometry";

type Corner = keyof Quad;
const CORNERS: Corner[] = ["tl", "tr", "br", "bl"];

interface CornerEditorProps {
  /** Downscaled preview of the captured frame. */
  src: string;
  quad: Quad;
  onChange: (q: Quad) => void;
}

/** The captured frame with four draggable corner handles. */
export function CornerEditor({ src, quad, onChange }: CornerEditorProps) {
  const boxRef = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState<Corner | null>(null);

  function toPoint(e: React.PointerEvent): Pt | null {
    const box = boxRef.current?.getBoundingClientRect();
    if (!box || box.width === 0) return null;
    return {
      x: Math.min(1, Math.max(0, (e.clientX - box.left) / box.width)),
      y: Math.min(1, Math.max(0, (e.clientY - box.top) / box.height)),
    };
  }

  function nearest(p: Pt): Corner {
    let best: Corner = "tl";
    let bestD = Infinity;
    for (const c of CORNERS) {
      const d = Math.hypot(quad[c].x - p.x, quad[c].y - p.y);
      if (d < bestD) {
        bestD = d;
        best = c;
      }
    }
    return best;
  }

  function onDown(e: React.PointerEvent) {
    const p = toPoint(e);
    if (!p) return;
    // Grab whichever corner is closest, so a finger doesn't have to land
    // exactly on a small handle.
    const c = nearest(p);
    setDragging(c);
    e.currentTarget.setPointerCapture(e.pointerId);
    move(c, p);
  }

  function onMove(e: React.PointerEvent) {
    if (!dragging) return;
    const p = toPoint(e);
    if (p) move(dragging, p);
  }

  function move(c: Corner, p: Pt) {
    const next = { ...quad, [c]: p };
    if (isConvex(next)) onChange(next);
  }

  const pts = CORNERS.map((c) => `${quad[c].x * 1000},${quad[c].y * 1000}`).join(" ");

  return (
    <div className="scan-corners">
      <div
        ref={boxRef}
        className="scan-corners__box"
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={() => setDragging(null)}
        onPointerCancel={() => setDragging(null)}
      >
        <img src={src} alt="Captured photo" draggable={false} />
        <svg viewBox="0 0 1000 1000" preserveAspectRatio="none" aria-hidden="true">
          <polygon points={pts} fill="rgba(80,200,120,0.15)" stroke="#5ad17f" strokeWidth={4} vectorEffect="non-scaling-stroke" />
        </svg>
        {CORNERS.map((c) => (
          <span
            key={c}
            className={`scan-corners__handle${dragging === c ? " is-active" : ""}`}
            style={{ left: `${quad[c].x * 100}%`, top: `${quad[c].y * 100}%` }}
          />
        ))}
      </div>
    </div>
  );
}
